import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PaymentCallback() {
  const [params] = useSearchParams();
  const reference = params.get("reference") || params.get("trxref");
  const [state, setState] = useState({ loading: true, status: null, amount: null, error: null });

  useEffect(() => {
    if (!reference) {
      setState({ loading: false, status: "invalid", error: "Missing reference in URL" });
      return;
    }
    axios.get(`${API}/payments/verify/${reference}`)
      .then((r) => setState({ loading: false, status: r.data.status, amount: r.data.amount_ghs }))
      .catch((err) => setState({ loading: false, status: "failed", error: err?.response?.data?.detail || "Verification failed" }));
  }, [reference]);

  const success = state.status === "success";

  return (
    <div data-testid="payment-callback-page" className="min-h-[70vh] flex items-center justify-center bg-faytex-pinkLight/40 px-4 py-24">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-hover border border-gray-100 p-10 text-center">
        {state.loading ? (
          <>
            <Loader2 className="w-10 h-10 text-faytex-pink animate-spin mx-auto" />
            <h1 className="font-heading text-3xl mt-6">Verifying your payment…</h1>
            <p className="text-sm text-faytex-muted mt-2">This usually takes a few seconds.</p>
          </>
        ) : success ? (
          <>
            <div className="w-14 h-14 rounded-full bg-faytex-mint/20 text-emerald-600 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <h1 data-testid="payment-success" className="font-heading text-3xl mt-6">Payment received.</h1>
            {state.amount != null && (
              <p className="mt-2 font-heading text-4xl text-faytex-pink">GH₵ {state.amount}</p>
            )}
            <p className="text-sm text-faytex-muted mt-4">
              Thank you! We&apos;ll be in touch with delivery details shortly.
            </p>
            <Link
              to="/shop"
              data-testid="payment-continue-shop"
              className="inline-flex items-center gap-2 mt-8 rounded-full bg-faytex-pink hover:bg-faytex-pinkHover text-white px-6 py-3 text-sm font-semibold"
            >
              Continue shopping <ArrowRight className="w-4 h-4" />
            </Link>
          </>
        ) : (
          <>
            <div className="w-14 h-14 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto">
              <XCircle className="w-7 h-7" />
            </div>
            <h1 data-testid="payment-failed" className="font-heading text-3xl mt-6">Payment {state.status || "failed"}</h1>
            <p className="text-sm text-faytex-muted mt-3">{state.error || "The transaction did not complete. No charge was made."}</p>
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 mt-8 rounded-full bg-faytex-ink hover:bg-faytex-pink text-white px-6 py-3 text-sm font-semibold"
            >
              Back to shop <ArrowRight className="w-4 h-4" />
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
